import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Bookmark,
  CornerDownRight,
  ExternalLink,
  Eye,
  Heart,
  Loader2,
  MessageCircle,
  RefreshCw,
  Send,
  Share2,
  Trash2,
  Users,
  X,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabaseClient';
import { invokeErrorMessage } from '../lib/supabaseFunctionError';
import type { SocialPlatform } from './SocialConnectButton';
import { BoostPostForm } from './marketing/BoostPostForm';

export type SocialComment = {
  id: string;
  commentId: string;
  parentCommentId: string | null;
  platform: string;
  postId: string;
  author: string;
  username: string | null;
  avatarUrl: string | null;
  text: string;
  likeCount: number;
  hidden: boolean;
  isOwn: boolean;
  status: 'new' | 'replied' | 'archived' | 'deleted';
  repliedAt: string | null;
  replyText: string | null;
  repliedBy: string | null;
  createdAt: string | null;
};

export type PostPerformance = {
  likes?: number;
  comments?: number;
  shares?: number;
  saves?: number;
  reach?: number;
  views?: number;
  engagement?: number;
  profile_visits?: number;
  new_follows?: number;
  clicks?: number;
  kind?: string;
};

type SheetPost = {
  id: string;
  post: string;
  postUrl: string | null;
  created: string | null;
  fullPicture: string | null;
  likeCount: number | null;
  commentsCount: number | null;
  ayrsharePostId: string | null;
  nexusHistoryId: string | null;
  reachCount?: number | null;
  viewsCount?: number | null;
  savesCount?: number | null;
  sharesCount?: number | null;
  profileVisits?: number | null;
  newFollows?: number | null;
};

const PLATFORM_LABEL: Record<string, string> = {
  facebook: 'Facebook',
  instagram: 'Instagram',
  linkedin: 'LinkedIn',
};

function cachedPerformance(post: SheetPost): PostPerformance | null {
  if (post.reachCount == null && post.viewsCount == null && post.savesCount == null && post.sharesCount == null) return null;
  return {
    likes: post.likeCount ?? undefined,
    comments: post.commentsCount ?? undefined,
    reach: post.reachCount ?? undefined,
    views: post.viewsCount ?? undefined,
    saves: post.savesCount ?? undefined,
    shares: post.sharesCount ?? undefined,
    profile_visits: post.profileVisits ?? undefined,
    new_follows: post.newFollows ?? undefined,
  };
}

function fmtCount(n: number): string {
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`;
  if (n >= 10_000) return `${(n / 1_000).toFixed(1).replace(/\.0$/, '')}K`;
  return n.toLocaleString();
}

function ago(iso: string | null): string {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const s = Math.max(0, Math.round((Date.now() - t) / 1000));
  if (s < 60) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m}m`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h}h`;
  const d = Math.round(h / 24);
  if (d < 7) return `${d}d`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

async function callComments<T = any>(body: Record<string, unknown>): Promise<T> {
  const { data, error } = await supabase.functions.invoke('ayrshare-social-comments', { body });
  if (error) throw new Error(data?.error || (await invokeErrorMessage(error, data)) || 'Something went wrong');
  if (data?.error) throw new Error(String(data.error));
  return data as T;
}

/** Author avatar: picture when the network gives one, otherwise an initial. */
function Avatar({ comment, size = 32 }: { comment: SocialComment; size?: number }) {
  const initial = (comment.author || '?').trim().charAt(0).toUpperCase();
  if (comment.avatarUrl) {
    return (
      <img
        src={comment.avatarUrl}
        alt=""
        width={size}
        height={size}
        className="rounded-full object-cover bg-gray-100 shrink-0"
        style={{ width: size, height: size }}
        referrerPolicy="no-referrer"
      />
    );
  }
  return (
    <span
      className={`inline-flex shrink-0 items-center justify-center rounded-full text-[12px] font-semibold ${comment.isOwn ? 'bg-blue-600 text-white' : 'bg-gray-200 text-gray-700'}`}
      style={{ width: size, height: size }}
    >
      {initial}
    </span>
  );
}

function ReplyBox({
  placeholder,
  busy,
  onSend,
  onCancel,
  autoFocus,
}: {
  placeholder: string;
  busy: boolean;
  onSend: (text: string) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}) {
  const [text, setText] = useState('');
  const ref = useRef<HTMLTextAreaElement | null>(null);
  useEffect(() => {
    if (autoFocus) ref.current?.focus();
  }, [autoFocus]);
  return (
    <div className="flex items-end gap-2">
      <textarea
        ref={ref}
        value={text}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if ((e.metaKey || e.ctrlKey) && e.key === 'Enter' && text.trim() && !busy) onSend(text.trim());
          if (e.key === 'Escape' && onCancel) onCancel();
        }}
        rows={1}
        placeholder={placeholder}
        className="flex-1 resize-none rounded-xl border border-gray-200 bg-white px-3 py-2 text-[13px] text-gray-900 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
        style={{ minHeight: 40, maxHeight: 120 }}
      />
      {onCancel ? (
        <button
          type="button"
          onClick={onCancel}
          className="h-10 w-10 inline-flex items-center justify-center rounded-xl text-gray-500 hover:bg-gray-100"
          aria-label="Cancel reply"
        >
          <X className="h-4 w-4" />
        </button>
      ) : null}
      <button
        type="button"
        disabled={busy || !text.trim()}
        onClick={() => text.trim() && onSend(text.trim())}
        className="h-10 w-10 inline-flex items-center justify-center rounded-xl bg-blue-600 text-white disabled:opacity-40"
        aria-label="Send reply"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
      </button>
    </div>
  );
}

function CommentRow({
  comment,
  replies,
  replyingTo,
  busyId,
  onReplyStart,
  onReplyCancel,
  onReplySend,
  onDelete,
  compact,
}: {
  comment: SocialComment;
  replies: SocialComment[];
  replyingTo: string | null;
  busyId: string | null;
  onReplyStart: (c: SocialComment) => void;
  onReplyCancel: () => void;
  onReplySend: (c: SocialComment, text: string) => void;
  onDelete: (c: SocialComment) => void;
  compact?: boolean;
}) {
  const canDelete = comment.platform !== 'linkedin' || comment.isOwn;
  return (
    <div className="flex gap-2.5">
      <Avatar comment={comment} />
      <div className="min-w-0 flex-1">
        <div className="flex items-baseline gap-2">
          <span className="text-[13px] font-semibold text-gray-900 truncate">
            {comment.isOwn ? 'You' : comment.author}
          </span>
          {comment.username && !comment.isOwn ? (
            <span className="text-[11px] text-gray-400 truncate">@{comment.username}</span>
          ) : null}
          <span className="text-[11px] text-gray-400 shrink-0">{ago(comment.createdAt)}</span>
        </div>
        <p className="text-[13px] text-gray-800 whitespace-pre-wrap break-words">{comment.text || <span className="text-gray-400">(no text)</span>}</p>
        <div className="mt-1 flex items-center gap-3 text-[11px] text-gray-500">
          {comment.likeCount > 0 ? (
            <span className="inline-flex items-center gap-1">
              <Heart className="h-3 w-3" />
              {fmtCount(comment.likeCount)}
            </span>
          ) : null}
          {!comment.isOwn ? (
            <button type="button" onClick={() => onReplyStart(comment)} className="font-semibold text-gray-700 hover:text-blue-700">
              Reply
            </button>
          ) : null}
          {canDelete ? (
            <button
              type="button"
              onClick={() => onDelete(comment)}
              disabled={busyId === comment.id}
              className="inline-flex items-center gap-1 text-gray-400 hover:text-red-600 disabled:opacity-50"
            >
              {busyId === comment.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
              Delete
            </button>
          ) : null}
          {comment.status === 'replied' && !comment.isOwn && replies.length === 0 ? (
            <span className="text-emerald-600">Replied</span>
          ) : null}
        </div>
        {replies.length > 0 ? (
          <div className="mt-2 space-y-2 border-l-2 border-gray-100 pl-3">
            {replies.map((r) => (
              <div key={r.id} className="flex gap-2">
                <Avatar comment={r} size={24} />
                <div className="min-w-0 flex-1">
                  <div className="flex items-baseline gap-2">
                    <span className="text-[12px] font-semibold text-gray-900 truncate">{r.isOwn ? 'You' : r.author}</span>
                    <span className="text-[11px] text-gray-400 shrink-0">{ago(r.createdAt)}</span>
                  </div>
                  <p className="text-[12.5px] text-gray-800 whitespace-pre-wrap break-words">{r.text}</p>
                  {(r.platform !== 'linkedin' || r.isOwn) && !compact ? (
                    <button
                      type="button"
                      onClick={() => onDelete(r)}
                      disabled={busyId === r.id}
                      className="mt-0.5 inline-flex items-center gap-1 text-[11px] text-gray-400 hover:text-red-600 disabled:opacity-50"
                    >
                      {busyId === r.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <Trash2 className="h-3 w-3" />}
                      Delete
                    </button>
                  ) : null}
                </div>
              </div>
            ))}
          </div>
        ) : null}
        {replyingTo === comment.id ? (
          <div className="mt-2">
            <ReplyBox
              autoFocus
              placeholder={`Reply to ${comment.author}…`}
              busy={busyId === comment.id}
              onSend={(text) => onReplySend(comment, text)}
              onCancel={onReplyCancel}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function useCommentActions(businessId: string | undefined, onChanged: () => void) {
  const [replyingTo, setReplyingTo] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  const reply = useCallback(async (c: SocialComment, text: string) => {
    if (!businessId) return;
    setBusyId(c.id);
    try {
      await callComments({ action: 'reply', businessId, commentId: c.id, text });
      toast.success('Reply posted');
      setReplyingTo(null);
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The reply was not posted');
    } finally {
      setBusyId(null);
    }
  }, [businessId, onChanged]);

  const remove = useCallback(async (c: SocialComment) => {
    if (!businessId) return;
    const who = c.isOwn ? 'your reply' : `${c.author}'s comment`;
    if (!window.confirm(`Delete ${who} from ${PLATFORM_LABEL[c.platform] || c.platform}? Everyone will stop seeing it.`)) return;
    setBusyId(c.id);
    try {
      await callComments({ action: 'delete', businessId, commentId: c.id });
      toast.success('Comment deleted');
      onChanged();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'The comment was not deleted');
    } finally {
      setBusyId(null);
    }
  }, [businessId, onChanged]);

  return { replyingTo, setReplyingTo, busyId, reply, remove };
}

function Stat({ icon: Icon, label, value }: { icon: typeof Heart; label: string; value: number | undefined }) {
  if (value == null) return null;
  return (
    <div className="flex flex-col items-center rounded-xl bg-gray-50 px-2 py-2 min-w-0">
      <span className="inline-flex items-center gap-1 text-[14px] font-semibold text-gray-900 tabular-nums">
        <Icon className="h-3.5 w-3.5 text-gray-500" />
        {fmtCount(value)}
      </span>
      <span className="text-[10px] uppercase tracking-wide text-gray-500">{label}</span>
    </div>
  );
}

/** Full-screen sheet for one live post: picture, caption, performance, and the comment thread with reply and delete. */
export function SocialPostSheet({
  open,
  onClose,
  businessId,
  platform,
  connectionId,
  post,
  onCommentsChanged,
}: {
  open: boolean;
  onClose: () => void;
  businessId: string | undefined;
  platform: SocialPlatform;
  connectionId: string | undefined;
  post: SheetPost | null;
  onCommentsChanged?: () => void;
}) {
  const [comments, setComments] = useState<SocialComment[]>([]);
  const [performance, setPerformance] = useState<PostPerformance | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const postIdRef = useRef<string | null>(null);

  const load = useCallback(async (withSpinner: boolean) => {
    if (!businessId || !post) return;
    if (withSpinner) setLoading(true);
    setLoadError(null);
    const cached = cachedPerformance(post);
    try {
      const data = await callComments<{ comments: SocialComment[]; analytics: PostPerformance | null }>({
        action: 'list',
        businessId,
        platform,
        connectionId,
        postId: post.id,
        ayrsharePostId: post.ayrsharePostId,
        historyId: post.nexusHistoryId,
        withAnalytics: !cached,
      });
      if (postIdRef.current !== post.id) return;
      setComments(data.comments || []);
      setPerformance(data.analytics || cached);
    } catch (e) {
      if (postIdRef.current !== post.id) return;
      if (cached) setPerformance(cached);
      setLoadError(e instanceof Error ? e.message : 'Could not load comments');
    } finally {
      if (postIdRef.current === post.id) setLoading(false);
    }
  }, [businessId, post, platform, connectionId]);

  useEffect(() => {
    postIdRef.current = post?.id ?? null;
    if (open && post) {
      setComments([]);
      setPerformance(cachedPerformance(post));
      void load(true);
    }
  }, [open, post?.id, load]); // eslint-disable-line react-hooks/exhaustive-deps

  const changed = useCallback(() => {
    void load(false);
    onCommentsChanged?.();
  }, [load, onCommentsChanged]);
  const actions = useCommentActions(businessId, changed);

  if (!open || !post) return null;
  const topLevel = comments.filter((c) => !c.parentCommentId);
  const repliesFor = (id: string) => comments.filter((c) => c.parentCommentId === id);
  const label = PLATFORM_LABEL[platform] || platform;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 backdrop-blur-[2px] p-0 sm:p-4"
      onClick={onClose}
    >
      <div
        className="w-full sm:max-w-lg bg-white rounded-t-[20px] sm:rounded-[20px] shadow-2xl overflow-hidden max-h-[92vh] sm:max-h-[85vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        <div className="flex items-center justify-between px-5 pt-4 pb-2">
          <div className="min-w-0">
            <h3 className="text-[15px] font-semibold text-gray-900">Post</h3>
            <p className="text-[12px] text-gray-500 truncate">
              {post.created ? new Date(post.created).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' }) : label}
              {post.postUrl ? (
                <>
                  {' · '}
                  <a href={post.postUrl} target="_blank" rel="noreferrer" className="inline-flex items-center gap-0.5 text-blue-600 hover:underline">
                    View on {label}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </>
              ) : null}
            </p>
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => void load(true)}
              disabled={loading}
              aria-label="Refresh comments"
              className="flex h-8 w-8 items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 disabled:opacity-50"
            >
              <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
            </button>
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="flex h-8 w-8 items-center justify-center rounded-full bg-gray-100 text-gray-500 active:bg-gray-200 touch-manipulation"
              style={{ WebkitTapHighlightColor: 'transparent' }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        <div className="overflow-y-auto px-5 pb-4 flex-1">
          <div className="flex gap-3">
            {post.fullPicture ? (
              <img
                src={post.fullPicture}
                alt=""
                className="h-24 w-24 shrink-0 rounded-xl object-cover border border-gray-100 bg-gray-50"
                referrerPolicy="no-referrer"
              />
            ) : null}
            <p className="text-[13px] text-gray-800 whitespace-pre-wrap line-clamp-6 flex-1">{post.post || 'No caption'}</p>
          </div>

          <div className="mt-3 grid grid-cols-4 gap-1.5">
            <Stat icon={Heart} label="Likes" value={performance?.likes ?? post.likeCount ?? undefined} />
            <Stat icon={MessageCircle} label="Comments" value={performance?.comments ?? post.commentsCount ?? undefined} />
            <Stat icon={Users} label="Reach" value={performance?.reach} />
            <Stat icon={Eye} label="Views" value={performance?.views} />
            <Stat icon={Bookmark} label="Saves" value={performance?.saves} />
            <Stat icon={Share2} label="Shares" value={performance?.shares} />
            <Stat icon={Users} label="Visits" value={performance?.profile_visits} />
            <Stat icon={Users} label="Follows" value={performance?.new_follows} />
          </div>

          {businessId ? (
            <BoostPostForm
              businessId={businessId}
              platform={platform}
              connectionId={connectionId}
              postId={post.id}
              ayrsharePostId={post.ayrsharePostId}
            />
          ) : null}

          <div className="mt-4 flex items-center justify-between">
            <h4 className="text-[13px] font-semibold text-gray-900">
              Comments{topLevel.length ? ` · ${topLevel.length}` : ''}
            </h4>
          </div>

          {loading && comments.length === 0 ? (
            <div className="mt-3 space-y-3">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex gap-2.5">
                  <div className="h-8 w-8 rounded-full bg-gray-100 animate-pulse" />
                  <div className="flex-1 space-y-1.5">
                    <div className="h-3 w-24 rounded bg-gray-100 animate-pulse" />
                    <div className="h-3 w-full rounded bg-gray-100 animate-pulse" />
                  </div>
                </div>
              ))}
            </div>
          ) : loadError ? (
            <p className="mt-3 rounded-xl bg-red-50 px-3 py-2 text-[12px] text-red-700">{loadError}</p>
          ) : topLevel.length === 0 ? (
            <p className="mt-3 text-[12.5px] text-gray-500">No comments yet. {label} refreshes comments about every 10 minutes.</p>
          ) : (
            <div className="mt-3 space-y-4">
              {topLevel.map((c) => (
                <CommentRow
                  key={c.id}
                  comment={c}
                  replies={repliesFor(c.commentId)}
                  replyingTo={actions.replyingTo}
                  busyId={actions.busyId}
                  onReplyStart={(x) => actions.setReplyingTo(x.id)}
                  onReplyCancel={() => actions.setReplyingTo(null)}
                  onReplySend={(x, text) => void actions.reply(x, text)}
                  onDelete={(x) => void actions.remove(x)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** "Needs a reply" strip: every unanswered comment across this account, with inline reply. */
export function SocialCommentsInbox({
  businessId,
  platform,
  connectionId,
  onOpenPost,
  refreshKey,
}: {
  businessId: string | undefined;
  platform: SocialPlatform;
  connectionId: string | undefined;
  onOpenPost?: (postId: string) => void;
  refreshKey?: number;
}) {
  const [items, setItems] = useState<SocialComment[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const refreshedRef = useRef<string | null>(null);

  const load = useCallback(async (refresh: boolean) => {
    if (!businessId || !connectionId) {
      setItems([]);
      return;
    }
    setLoading(true);
    try {
      const data = await callComments<{ comments: SocialComment[] }>({
        action: 'inbox',
        businessId,
        platform,
        connectionId,
        refresh,
        limit: 40,
      });
      setItems(data.comments || []);
    } catch {
      // The strip is best-effort; the post sheet still works.
    } finally {
      setLoading(false);
      setLoadedOnce(true);
    }
  }, [businessId, connectionId, platform]);

  useEffect(() => {
    if (!connectionId) return;
    // One network refresh per account per mount; later loads read the mirror.
    const key = `${businessId}:${connectionId}`;
    const refresh = refreshedRef.current !== key;
    refreshedRef.current = key;
    void load(refresh);
  }, [businessId, connectionId, load, refreshKey]);

  const changed = useCallback(() => void load(false), [load]);
  const actions = useCommentActions(businessId, changed);

  const archive = async (c: SocialComment) => {
    if (!businessId) return;
    try {
      await callComments({ action: 'archive', businessId, commentId: c.id });
      setItems((prev) => prev.filter((x) => x.id !== c.id));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not dismiss');
    }
  };

  if (!connectionId) return null;
  if (loadedOnce && items.length === 0 && !loading) return null;
  const shown = expanded ? items : items.slice(0, 3);

  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5 text-gray-900">
          <MessageCircle className="h-4 w-4 text-gray-500" />
          <h3 className="text-[13px] font-semibold" style={{ letterSpacing: '-0.01em' }}>
            Needs a reply
          </h3>
          {items.length ? (
            <span className="ml-1 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-rose-500 px-1.5 text-[11px] font-semibold text-white">
              {items.length}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          onClick={() => void load(true)}
          disabled={loading}
          className="inline-flex items-center gap-1 text-[12px] font-medium text-gray-500 hover:text-gray-800 disabled:opacity-50"
        >
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? 'animate-spin' : ''}`} />
          Check now
        </button>
      </div>
      <div className="bg-white border border-gray-200 rounded-xl divide-y divide-gray-100">
        {loading && items.length === 0 ? (
          <div className="px-4 py-3 text-[12.5px] text-gray-500 inline-flex items-center gap-2">
            <Loader2 className="h-3.5 w-3.5 animate-spin" />
            Checking {PLATFORM_LABEL[platform]} for new comments…
          </div>
        ) : null}
        {shown.map((c) => (
          <div key={c.id} className="px-4 py-3">
            <CommentRow
              compact
              comment={c}
              replies={[]}
              replyingTo={actions.replyingTo}
              busyId={actions.busyId}
              onReplyStart={(x) => actions.setReplyingTo(x.id)}
              onReplyCancel={() => actions.setReplyingTo(null)}
              onReplySend={(x, text) => void actions.reply(x, text)}
              onDelete={(x) => void actions.remove(x)}
            />
            <div className="mt-1.5 flex items-center gap-3 pl-[42px] text-[11px]">
              {onOpenPost && c.postId ? (
                <button type="button" onClick={() => onOpenPost(c.postId)} className="inline-flex items-center gap-1 text-gray-500 hover:text-blue-700">
                  <CornerDownRight className="h-3 w-3" />
                  Open post
                </button>
              ) : null}
              <button type="button" onClick={() => void archive(c)} className="text-gray-400 hover:text-gray-700">
                Dismiss
              </button>
            </div>
          </div>
        ))}
        {items.length > 3 ? (
          <button
            type="button"
            onClick={() => setExpanded((v) => !v)}
            className="w-full px-4 py-2.5 text-[12px] font-semibold text-blue-700 hover:bg-blue-50/60"
          >
            {expanded ? 'Show fewer' : `Show all ${items.length}`}
          </button>
        ) : null}
      </div>
    </section>
  );
}
